import React, { createContext, useCallback, useContext, useMemo } from 'react';
import toast from 'react-hot-toast';
import { AlertCircle, CheckCircle2, Info, TriangleAlert } from 'lucide-react';

/**
 * Transient feedback messages.
 *
 * The public API — `const { showSnackbar } = useSnackbar()` and
 * `showSnackbar(message, severity)` — is unchanged; roughly sixty call sites
 * depend on it. What changed is underneath: this was the last real consumer of
 * `@mui/material`, and it rendered a *second* toast system alongside the
 * `react-hot-toast` Toaster already mounted in main.tsx, so two visually
 * unrelated notifications could appear at once. It now renders through that
 * single Toaster, which let MUI and Emotion leave the bundle entirely.
 *
 * `AlertColor` is re-exported so existing imports keep resolving.
 */
export type AlertColor = 'success' | 'info' | 'warning' | 'error';

interface SnackbarContextType {
  showSnackbar: (message: string, severity?: AlertColor) => void;
}

const SnackbarContext = createContext<SnackbarContextType>({
  showSnackbar: () => {},
});

export const useSnackbar = () => useContext(SnackbarContext);

const ICONS: Record<AlertColor, React.ReactNode> = {
  success: <CheckCircle2 size={18} className="text-success" aria-hidden="true" />,
  info: <Info size={18} className="text-info" aria-hidden="true" />,
  warning: <TriangleAlert size={18} className="text-warning" aria-hidden="true" />,
  error: <AlertCircle size={18} className="text-error" aria-hidden="true" />,
};

export const SnackbarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const showSnackbar = useCallback((message: string, severity: AlertColor = 'info') => {
    toast.custom(
      (instance) => (
        <div
          // Errors are announced immediately; everything else waits for a pause
          // so a success toast cannot interrupt what is being read.
          role={severity === 'error' ? 'alert' : 'status'}
          aria-live={severity === 'error' ? 'assertive' : 'polite'}
          className={[
            'pointer-events-auto flex max-w-md items-start gap-2.5 rounded-md border border-border',
            'bg-background-card px-4 py-3 shadow-md',
            instance.visible ? 'animate-fade-up' : 'opacity-0',
          ].join(' ')}
        >
          <span className="mt-0.5 shrink-0">{ICONS[severity]}</span>
          <p className="text-caption text-text-primary">{message}</p>
        </div>
      ),
      { duration: severity === 'error' ? 5000 : 3000 },
    );
  }, []);

  const value = useMemo(() => ({ showSnackbar }), [showSnackbar]);

  return <SnackbarContext.Provider value={value}>{children}</SnackbarContext.Provider>;
};
